import { useEffect, useState } from "react";
import { Cpu, Film, Upload, AlertTriangle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/pages/Dashboard";
import { useAppConfig } from "@/context/ConfigContext";
import { agentVideo } from "@/lib/agent";

export default function VideoGeneration() {
  const { models } = useAppConfig();
  const videoModels = models.filter((m) => (m.capabilities || []).includes("image-to-video"));
  const [selectedModel, setSelectedModel] = useState("");
  const [prompt, setPrompt] = useState("Movimiento de cámara suave y cinematográfico, natural, manteniendo exactamente la composición y el aspecto de la fotografía.");
  const [imageData, setImageData] = useState("");
  const [preview, setPreview] = useState("");
  const [frames, setFrames] = useState(9);
  const [fps, setFps] = useState(24);
  const [seed, setSeed] = useState(42);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (!loading) return;
    const started = Date.now();
    const timer = setInterval(() => setElapsedSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [loading]);

  const chooseFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setResult({ ok: false, error: "Selecciona una imagen válida." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || "");
      setImageData(value);
      setPreview(value);
      setResult(null);
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!imageData || !prompt.trim() || !videoModels.length || loading) return;
    setElapsedSeconds(0);
    setLoading(true);
    setResult(null);
    const data = await agentVideo({
      model: selectedModel || videoModels[0].id,
      image_data_url: imageData,
      prompt: prompt.trim(),
      frames,
      fps,
      seed,
    }).catch((e) => ({ ok: false, error: e?.message || "Error de red" }));
    setResult(data);
    setLoading(false);
  };

  const duration = (Number(frames) / Number(fps || 24)).toFixed(1);

  useEffect(() => {
    if (videoModels.length && !videoModels.some((m) => m.id === selectedModel)) setSelectedModel(videoModels[0].id);
  }, [videoModels, selectedModel]);

  return (
    <div className="krai-enter">
      <PageHeader title="Vídeo" subtitle="Image-to-video local · LTX-2.3 MLX · Apple Silicon" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-md border border-[#262626] bg-[#121212] p-4">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">Imagen de entrada</div>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-sm border border-dashed border-[#404040] bg-[#0a0a0a] p-5 text-sm text-[#a3a3a3] hover:text-white">
            <Upload className="h-4 w-4" /> Seleccionar foto
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseFile} className="hidden" />
          </label>
          {preview && <img src={preview} alt="Fotograma inicial" className="max-h-72 w-full rounded-md object-contain bg-black" />}
          <select value={selectedModel || videoModels[0]?.id || ""} onChange={(e) => setSelectedModel(e.target.value)} className="h-10 w-full rounded-md border border-[#262626] bg-[#0a0a0a] px-3 text-sm">
            {videoModels.map((m) => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
          </select>
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe el movimiento que quieres…" className="min-h-28 border-[#262626] bg-[#0a0a0a]" />
          <div className="grid grid-cols-3 gap-2">
            <label className="text-xs text-[#a3a3a3]">Fotogramas<Input aria-label="Fotogramas" type="number" min="9" max="97" step="8" value={frames} onChange={(e) => setFrames(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" /></label>
            <label className="text-xs text-[#a3a3a3]">FPS<Input aria-label="FPS" type="number" min="12" max="30" value={fps} onChange={(e) => setFps(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" /></label>
            <label className="text-xs text-[#a3a3a3]">Semilla<Input aria-label="Semilla" type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} className="border-[#262626] bg-[#0a0a0a]" /></label>
          </div>
          <div className="font-mono text-[10px] text-[#737373]">Duración aproximada: {duration}s · más fotogramas requieren más tiempo y memoria</div>
          <Button onClick={generate} disabled={!imageData || !prompt.trim() || !videoModels.length || loading} className="w-full rounded-sm bg-white text-black hover:bg-neutral-200">
            {loading ? <Cpu className="mr-2 h-4 w-4 animate-pulse" /> : <Film className="mr-2 h-4 w-4" />}
            {loading ? `Generando vídeo… ${Math.floor(elapsedSeconds / 60)}:${String(elapsedSeconds % 60).padStart(2, "0")}` : "Generar vídeo"}
          </Button>
          {!videoModels.length && <div className="font-mono text-xs text-amber-300">El motor de vídeo todavía no está instalado en este Mac.</div>}
        </div>

        <div className="min-h-[500px] rounded-md border border-[#262626] bg-[#121212] p-4">
          {!result && <div className="flex h-full min-h-[460px] items-center justify-center text-[#737373]">{loading ? "Procesando el vídeo en tu Mac. Mantén esta ventana abierta; no se utiliza ninguna API de pago." : "El vídeo generado aparecerá aquí."}</div>}
          {result?.ok && (
            <div className="space-y-3">
              <video src={result.video_data_url} controls autoPlay loop playsInline className="w-full rounded-md bg-black" />
              <a href={result.video_data_url} download="krai-video.mp4" className="inline-flex items-center gap-2 rounded-sm border border-[#404040] px-3 py-2 text-sm hover:bg-[#1a1a1a]">
                <Download className="h-4 w-4" /> Descargar MP4
              </a>
            </div>
          )}
          {result && !result.ok && <div className="flex items-start gap-3 rounded-md border border-red-500/40 bg-red-500/10 p-3"><AlertTriangle className="h-4 w-4 text-red-400" /><span className="font-mono text-sm text-red-200">{result.error}</span></div>}
        </div>
      </div>
    </div>
  );
}
